/**
 * Unified API & Telemetry Aggregator for the Daily Overview ("Front Page").
 *
 * Combines:
 *   1. Strategic Compass ("Where We Are Going" + Active Bets / Weird Ideas)
 *   2. Workspace & Historical Agent Activity Ledger (`src/data/agent_activity_log.json` + `/api/daily-overview`)
 *   3. Paid Ads Autopilot (`ads_optimizer` — 3-tier runs, proposals + rationale, monthly P&L ledger)
 *   4. SEO Autonomous Engine (`seo_engine` — scored news headlines, content pipeline, buffer status, alerts)
 *   5. Email Auto-Scheduler (`AutoScheduleQueue` — SendEngine v2 score breakdown, reasoning, guardrail holds)
 *   6. Live Business Pulse (Today vs Yesterday & 7d revenue, conversions, active tasks)
 */
import { API_BASE_URL } from '../config';
import shippedCompass from '../data/business_compass.json';
import shippedAgentLogs from '../data/agent_activity_log.json';
import shippedTasks from '../data/tasks.json';
import { fetchGeoOptimizerDashboard, GeoOptimizerDashboardResponse, OptimizerProposal } from './geoOptimizerApi';
import { seoApi } from './seoApi';
import { dashboardApi } from './dashboardApi';
import type { ContentItem, SeoOverview } from '../pages/seo/types';

export type AgentDomain = 'all' | 'ads' | 'seo' | 'email' | 'deliverability' | 'leads' | 'workspace' | 'strategy';
export type AgentActionStatus = 'applied' | 'pending_review' | 'blocked' | 'info' | 'rolled_back';

export interface ScoreBreakdown {
  revenue?: number;
  crossEsp?: number;
  clickQuality?: number;
  recency?: number;
  unsub?: number;
  openRate?: number;
}

export interface AgentActivityEntry {
  id: string;
  timestamp: string;
  agentName: string;
  domain: Exclude<AgentDomain, 'all'>;
  geo: string;
  actionType: string;
  status: AgentActionStatus;
  title: string;
  summary: string;
  reasoning: string;
  signals: string[];
  guardrails: string[];
  metrics?: {
    impactLabel?: string;
    confidencePct?: number;
    score?: number;
    scoreBreakdown?: ScoreBreakdown;
    platform?: string;
    espId?: string;
    [key: string]: any;
  };
  deepLink: string;
  commitHash?: string | null;
}

export interface StrategicBet {
  id: string;
  title: string;
  hypothesis: string;
  domain: Exclude<AgentDomain, 'all'>;
  geo: string;
  status: 'active' | 'testing' | 'weird_idea' | 'validated' | 'paused';
  metricLabel: string;
  deepLink?: string;
  updatedAt?: string;
}

export interface NorthStarData {
  id: string;
  title: string;
  summary: string;
  pillars: string[];
  weirdNote: string;
  updatedBy: string;
  updatedAt: string;
}

export interface BusinessCompassState {
  northStar: NorthStarData;
  bets: StrategicBet[];
}

export interface AttentionItem {
  id: string;
  domain: Exclude<AgentDomain, 'all'>;
  geo: string;
  priority: 'critical' | 'high' | 'medium';
  title: string;
  subtitle: string;
  reasoning: string;
  impactLabel?: string;
  deepLink: string;
  createdAt?: string;
}

export interface PillarSummary {
  domain: Exclude<AgentDomain, 'all'>;
  title: string;
  status: 'healthy' | 'attention' | 'warning';
  headlineMetric: string;
  subMetric: string;
  lastAgentAction: string;
  deepLink: string;
}

export interface AgentForumMessage {
  id: number | string;
  speakerId: string;
  speakerName: string;
  domain: string;
  modelUsed: string;
  message: string;
  evidence: string[];
  replyToSpeaker?: string | null;
  createdAt?: string | null;
}

export interface AgentForumThread {
  id: string;
  threadType: 'daily_roundtable' | 'cross_channel_insight' | 'blocker_question' | 'anomaly_alert';
  status: 'open' | 'needs_human' | 'resolved' | 'actioned';
  priority: 'critical' | 'high' | 'normal';
  geo: string;
  title: string;
  contextSummary: string;
  questionForHuman?: string | null;
  suggestedOptions: string[];
  humanDecision?: string | null;
  decidedBy?: string | null;
  decidedAt?: string | null;
  linkedActions: Array<{ domain: string; label: string }>;
  modelUsed: string;
  costUsd: number;
  notifiedAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  messages: AgentForumMessage[];
}

export interface AgentReflectionEntry {
  id: number;
  reflectionBatchId: string;
  speakerId: 'fox' | 'eskild' | 'bruun' | 'frederik' | 'amalie' | string;
  speakerName: string;
  domain: string;
  geo: string;
  triggerType: 'scheduled_2h' | 'founder_steering' | 'peer_mention' | 'manual_reflect' | string;
  lensTitle: string;
  statsInspected: string[];
  thoughtProcess: string;
  hypothesis?: string | null;
  statusTag: 'watching' | 'promote_to_roundtable' | 'needs_founder' | 'answered_peer' | string;
  mentionTarget?: string | null;
  mentionQuestion?: string | null;
  replyToReflectionId?: number | null;
  replyToSummary?: string | null;
  founderNote?: string | null;
  executableAction?: {
    action_type: 'approve_email_campaign' | 'approve_ads_proposal' | 'promote_seo_article' | 'acknowledge_seo_alert' | string;
    target_id: string;
    button_label: string;
  } | null;
  modelUsed: string;
  costUsd: number;
  createdAt?: string | null;
}

export interface AgentSteeringNote {
  id: number;
  targetSpeakerId: string;
  note: string;
  status: string;
  createdAt?: string | null;
}

export interface AgentRosterItem {
  id: string;
  name: string;
  domain: string;
  type: 'llm' | 'math';
  primaryModel: string;
  activeModel: string;
  schedule: string;
  role: string;
}

export interface AiBudgetStatus {
  mtdBedrockUsd: number;
  mtdDataforseoUsd: number;
  totalMtdUsd: number;
  softCapUsd: number;
  hardCapUsd: number;
  activeTier: 'flagship_opus_5_5' | 'throttled_haiku_4_5' | 'hard_capped';
  notifyEmail: string;
  notifyOnNeedsHuman: boolean;
  notifyOnDailyDigest: boolean;
  slackWebhookUrl?: string;
  slackConfigured?: boolean;
  notifySlackOnUrgent?: boolean;
  spendByJobUsd: Record<string, number>;
  agentRoster: AgentRosterItem[];
}

export interface CompetitorOfferLeaderboardItem {
  offer_domain: string;
  affiliate_network: string;
  sends_count: number;
  distinct_competitors: number;
  inbox_count: number;
  spam_count: number;
  competitor_senders: string;
  sample_subjects: string;
  last_seen_at?: string;
}

export interface CompetitorInboxEmailItem {
  id: number;
  received_at: string;
  folder_placement: 'inbox' | 'spam';
  sender_name: string;
  sender_email: string;
  sender_domain: string;
  esp_detected: string;
  geo: string;
  subject: string;
  cta_resolved_url?: string | null;
  offer_domain?: string | null;
  affiliate_network?: string | null;
}

export interface CompetitorWatchData {
  monitoredInbox: string;
  totalCompetitorEmails: number;
  topOffersLeaderboard: CompetitorOfferLeaderboardItem[];
  recentEmails: CompetitorInboxEmailItem[];
}

export interface SendoutQaIssueItem {
  category:
    | 'incentive_mismatch'
    | 'brand_link_mismatch'
    | 'language_danisme'
    | 'merge_tag_or_formatting'
    | 'compliance'
    | 'deliverability_or_link'
    | string;
  severity: 'warning' | 'critical' | string;
  detail: string;
}

export interface SendoutQaAuditEntry {
  id: number;
  message_id: string;
  seed_account: string;
  received_at: string;
  folder_placement: 'inbox' | 'spam';
  sender_name: string;
  sender_email: string;
  sender_domain: string;
  esp_detected: string;
  geo: string;
  subject: string;
  preheader?: string | null;
  go_url?: string | null;
  offer_slug?: string | null;
  campaign_id?: string | null;
  version_id?: string | null;
  afima_send_id?: string | null;
  lead_id_value?: string | null;
  brand_name?: string | null;
  expected_loan_type?: string | null;
  expected_max_amount?: string | null;
  spf_dkim_status?: string | null;
  qa_status: 'pass' | 'warning' | 'critical';
  issues: SendoutQaIssueItem[];
  bruun_summary_da: string;
  suggested_fix?: string | null;
  audited_at?: string | null;
}

export interface SendoutQaWatchData {
  seedAccountsConfigured: string[];
  totalAudited: number;
  passCount: number;
  warningCount: number;
  criticalCount: number;
  inboxCount: number;
  spamCount: number;
  recentAudits: SendoutQaAuditEntry[];
}

export interface SprintExperimentTrack {
  track: string;
  current_baseline: string;
  testable_ideas: string[];
}

export interface AgentStandingDirective {
  id: string;
  speakerId: string;
  domain: string;
  geo: string;
  title: string;
  directiveText: string;
  focusChannels: string[];
  suppressedChannels: string[];
  sprintLenses: string[];
  experimentTracks: SprintExperimentTrack[];
  status: 'active' | 'completed' | 'paused' | string;
  startsAt?: string | null;
  activeUntil?: string | null;
  createdBy?: string;
  updatedAt?: string | null;
}

export interface SoloExperimentVariant {
  id: string;
  label: string;
  track: string;
  destinationPath: string;
  creativeHook: string;
  status: 'active' | 'paused' | 'winner' | 'queued_next' | string;
  clicks: number;
  spendNok: number;
  cpcNok: number;
  lenderClickouts: number;
  clickoutRatePct: number;
}

export interface SoloMetaCreative {
  id: number;
  geo: string;
  assetKey: string;
  angle: string;
  sizeLabel: string;
  headline: string;
  primaryText: string;
  complianceText: string;
  hasLegalFooter: boolean;
  status: string;
  clicks: number;
  spendNok: number;
  ctrPct: number;
}

export interface SoloExperiment {
  id: string;
  title: string;
  channel: string;
  geo: string;
  status: 'collecting_data' | 'ready_for_verdict' | 'paused' | string;
  startsAt: string;
  endsAt: string;
  targetClicks: number;
  currentClicks: number;
  progressPct: number;
  currentSpendNok: number;
  stopLossRule: string;
  successCriteria: string;
  snoozedChannels: string[];
  variants: SoloExperimentVariant[];
  metaCreatives: SoloMetaCreative[];
  verdictNote: string;
  updatedAt?: string | null;
}

export interface SoloSeoDeliverable {
  id: number;
  market: string;
  type: string;
  title: string;
  slug: string;
  targetKeyword: string;
  status: string;
  wordCount: number;
  qualityScore: number;
  liveUrl: string;
  updatedAt?: string | null;
}

export interface SoloEmailDeliverable {
  afimaCampaignId: string;
  geo: string;
  title: string;
  summary: string;
  rawStatus: string;
  score: number;
  reasoning: string;
}

export interface SoloAdsDeliverable {
  id: number;
  geo: string;
  platform: string;
  proposalType: string;
  title: string;
  rationale: string;
  impactDkk: number;
}

export interface SoloOperatorViewData {
  activeExperiments: SoloExperiment[];
  deliverablesQueue: {
    seoArticles: SoloSeoDeliverable[];
    emailSlots: SoloEmailDeliverable[];
    adsProposals: SoloAdsDeliverable[];
    metaCreatives: SoloMetaCreative[];
  };
  autopilotSummary: {
    negKeywordsApplied7d: number;
    otherAdsActionsApplied7d: number;
    suppressedAdsChannels: string[];
    seoPublished7d: number;
    openSeoAlerts: Array<{
      id: number;
      market: string;
      alertType: string;
      severity: string;
      title: string;
    }>;
  };
}

export interface DailyOverviewData {
  soloOperatorView?: SoloOperatorViewData;
  standingDirectives?: AgentStandingDirective[];
  competitorWatch?: CompetitorWatchData;
  sendoutQaWatch?: SendoutQaWatchData;
  generatedAt: string;
  compass: BusinessCompassState;
  forumThreads: AgentForumThread[];
  agentReflections: AgentReflectionEntry[];
  pendingSteeringNotes: AgentSteeringNote[];
  aiBudget: AiBudgetStatus;
  pulse: {
    revenueTodayDkk: number;
    revenueYesterdayDkk: number;
    revenue7dDkk: number;
    conversionsToday: number;
    conversionsYesterday: number;
    noAdsPacingRegime: string;
    noAdsMtdSpendDkk: number;
    noAdsMtdRevRiskAdjDkk: number;
    noAdsRemainingLossBufferDkk: number;
    noAdsTodayCapDkk: number;
    noAdsPendingProposals: number;
    noAdsAppliedProposals: number;
    seoBufferDaysSe: number;
    seoBufferDaysNo: number;
    seoReviewCount: number;
    emailSentToday: number;
    emailScheduledToday: number;
    emailHeldToday: number;
    urgentTasksCount: number;
    alignmentNotes: Array<{ tone: 'positive' | 'warning' | 'neutral'; text: string }>;
  };
  pillars: PillarSummary[];
  entries: AgentActivityEntry[];
  attentionQueue: AttentionItem[];
  liveSourcesConnected: {
    ads: boolean;
    seo: boolean;
    email: boolean;
    revenue: boolean;
  };
}

const COMPASS_STORAGE_KEY = 'afima_business_compass_v1';
const LOCAL_AGENT_LOGS_KEY = 'afima_local_agent_logs_v1';
const TASKS_STORAGE_KEY = 'afima_action_center_tasks_v1';
const FORUM_DECISIONS_KEY = 'afima_forum_decisions_v1';

const DEFAULT_FORUM_THREADS: AgentForumThread[] = [];

const DEFAULT_AI_BUDGET: AiBudgetStatus = {
  mtdBedrockUsd: 0,
  mtdDataforseoUsd: 0,
  totalMtdUsd: 0,
  softCapUsd: 100.0,
  hardCapUsd: 125.0,
  activeTier: 'flagship_opus_5_5',
  notifyEmail: 'kristoffer@afima.dk',
  notifyOnNeedsHuman: true,
  notifyOnDailyDigest: true,
  spendByJobUsd: {},
  agentRoster: [
    {
      id: 'agent_forum',
      name: 'Amalie Ryberg — Chief of Staff (War Room & Oversight)',
      domain: 'strategy',
      type: 'llm',
      primaryModel: 'Claude Sonnet 4.5 / Opus 5.5',
      activeModel: 'Claude Sonnet 4.5',
      schedule: 'Daily 07:05 UTC + On-Demand',
      role: 'Audits whether ideas actually get accomplished, challenges cross-channel relevance, checks if managers need tools/access, and evaluates if the roundtable is working.',
    },
    {
      id: 'seo_news_writer',
      name: 'Eskild — Head of SEO (Newsjacking & Article Engine)',
      domain: 'seo',
      type: 'llm',
      primaryModel: 'Claude Opus 5.5 / Sonnet 4.5',
      activeModel: 'Claude Opus 5.5',
      schedule: 'Daily 05:15 UTC',
      role: 'Scans Nordic central-bank/finance news (0–100) and drafts compliant SE/NO loan & refinancing guides.',
    },
    {
      id: 'ads_creative_strategist',
      name: 'Fox — Head of Paid Ads (Creative Strategist Tier 3)',
      domain: 'ads',
      type: 'llm',
      primaryModel: 'Claude Opus 5.5 / Sonnet 4.5',
      activeModel: 'Claude Opus 5.5',
      schedule: 'Every 72 hours',
      role: 'Audits Google/Microsoft RSA & Meta creative fatigue and drafts Finansavtaleloven-compliant ad variants.',
    },
    {
      id: 'daily_report_analyzer',
      name: 'Frederik Hartmann — Deliverability & Engagement Analyst',
      domain: 'deliverability',
      type: 'llm',
      primaryModel: 'Claude Sonnet 4.5',
      activeModel: 'Claude Sonnet 4.5',
      schedule: 'Daily 08:00 UTC + Roundtable',
      role: 'Diagnoses 7-day ESP open/click trends, segment health, and tests which campaign types drive engagement.',
    },
    {
      id: 'email_copywriter',
      name: 'Bruun — Head of Email (Copywriter & Edition Generator)',
      domain: 'email',
      type: 'llm',
      primaryModel: 'Claude Sonnet 4.5',
      activeModel: 'Claude Sonnet 4.5',
      schedule: 'On-Demand / Weekly Batch',
      role: 'Writes & repairs localized TA1–TA5 email editions and subject lines across SE, NO, FI, and DE.',
    },
    {
      id: 'incoming_reply_classifier',
      name: 'Bruun — Sub-Worker: Reply & Unsubscribe Triage',
      domain: 'email',
      type: 'llm',
      primaryModel: 'Claude Haiku 4.5',
      activeModel: 'Claude Haiku 4.5',
      schedule: 'Real-Time Webhook',
      role: 'Fast/cheap classification of incoming Missive replies to auto-process unsubscribes.',
    },
    {
      id: 'ads_solver_guardian',
      name: 'Fox — Sub-Workers: Intraday Guardian (4h) & Daily Solver (24h)',
      domain: 'ads',
      type: 'math',
      primaryModel: 'Bayesian P&L Solver ($0 LLM)',
      activeModel: 'Bayesian P&L Solver ($0 LLM)',
      schedule: 'Every 4h + Daily 06:20 UTC',
      role: 'Enforces 60k DKK cap & 20k DKK loss buffer, shifts budgets (±15%), and blocks junk search terms.',
    },
    {
      id: 'sendengine_scheduler',
      name: 'Bruun — Sub-Worker: SendEngine v2 Auto-Scheduler',
      domain: 'email',
      type: 'math',
      primaryModel: 'Posterior RPS + UCB Scorer ($0 LLM)',
      activeModel: 'Posterior RPS + UCB Scorer ($0 LLM)',
      schedule: 'Mon 05:00 UTC + Daily 05:30 & 13:30 UTC',
      role: 'Scores campaigns 0–100 across 10+ ESPs, holds <40 or cooldown conflicts, dispatches winners.',
    },
    {
      id: 'seo_publisher_radar',
      name: 'Eskild — Sub-Workers: 100-Pt Quality Gate, Publisher & SERP Radar',
      domain: 'seo',
      type: 'math',
      primaryModel: '100-Pt Quality Gate ($0 LLM)',
      activeModel: '100-Pt Quality Gate ($0 LLM)',
      schedule: 'Every 15m + Daily Radar',
      role: 'Grades statutory compliance & links, publishes jittered slots (1.5/d), tracks competitor SERPs.',
    },
    {
      id: 'deliverability_autopruner',
      name: 'Frederik Hartmann — Sub-Workers: 90/10 Pruner, DeBounce & Watchdog',
      domain: 'deliverability',
      type: 'math',
      primaryModel: 'Rule Engine ($0 LLM)',
      activeModel: 'Rule Engine ($0 LLM)',
      schedule: 'Daily 01:30–04:00 UTC + 6h Intake + 3m Watchdog',
      role: 'Enforces 90/10 probation split, 14d/30d pruning, converter protection, and 2,500+ clean leads/GEO.',
    },
  ],
};

export function loadCompassState(): BusinessCompassState {
  const defaultState: BusinessCompassState = {
    northStar: {
      ...(shippedCompass as any).northStar,
      weirdNote: '',
    },
    bets: [],
  };
  try {
    const raw = localStorage.getItem(COMPASS_STORAGE_KEY);
    if (!raw) return defaultState;
    const saved = JSON.parse(raw);
    const cleanedWeirdNote =
      typeof saved?.northStar?.weirdNote === 'string' &&
      saved.northStar.weirdNote.includes('Microsoft Ads (Bing) in Norway continues outperforming Google')
        ? ''
        : saved?.northStar?.weirdNote || '';
    return {
      northStar: {
        ...defaultState.northStar,
        ...(saved.northStar || {}),
        weirdNote: cleanedWeirdNote,
      },
      bets: [],
    };
  } catch {
    return defaultState;
  }
}

export async function saveCompassState(next: BusinessCompassState): Promise<BusinessCompassState> {
  const stamped: BusinessCompassState = {
    ...next,
    northStar: {
      ...next.northStar,
      updatedAt: new Date().toISOString(),
    },
  };
  try {
    localStorage.setItem(COMPASS_STORAGE_KEY, JSON.stringify(stamped));
  } catch {
    // ignore storage quota errors
  }
  try {
    await fetch(`${API_BASE_URL}/api/daily-overview/compass`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: stamped.northStar.id,
        itemType: 'north_star',
        title: stamped.northStar.title,
        body: stamped.northStar.pillars.join('\n'),
        domain: 'strategy',
        geo: 'ALL',
        status: 'active',
        metricLabel: stamped.northStar.weirdNote,
      }),
    });
  } catch {
    // local storage remains source of truth when offline
  }
  return stamped;
}

export function appendLocalAgentLog(entry: Omit<AgentActivityEntry, 'id' | 'timestamp'>): AgentActivityEntry {
  const full: AgentActivityEntry = {
    ...entry,
    id: `local-${Date.now()}`,
    timestamp: new Date().toISOString(),
  };
  try {
    const raw = localStorage.getItem(LOCAL_AGENT_LOGS_KEY);
    const list: AgentActivityEntry[] = raw ? JSON.parse(raw) : [];
    const updated = [full, ...list].slice(0, 100);
    localStorage.setItem(LOCAL_AGENT_LOGS_KEY, JSON.stringify(updated));
  } catch {
    // ignore
  }
  fetch(`${API_BASE_URL}/api/daily-overview/log`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(full),
  }).catch(() => null);
  return full;
}

function mapAdsProposalToEntry(p: OptimizerProposal): AgentActivityEntry {
  const statusMap: Record<string, AgentActionStatus> = {
    applied: 'applied',
    pending: 'pending_review',
    rejected: 'blocked',
    rolled_back: 'rolled_back',
    failed: 'blocked',
  };
  const impact = Number(p.expected_daily_margin_impact_dkk || 0);
  const confPct = Math.round(Number(p.confidence || 0.75) * 100);
  const platLabel = p.platform === 'google' ? 'Google Ads' : p.platform === 'microsoft' ? 'Microsoft Ads' : 'Meta Ads';

  return {
    id: `ads-proposal-${p.id}`,
    timestamp: p.decided_at || p.created_at || new Date().toISOString(),
    agentName: `Fox · Paid Ads (${platLabel})`,
    domain: 'ads',
    geo: (p.geo || 'no').toUpperCase(),
    actionType: p.proposal_type,
    status: statusMap[p.status] || 'pending_review',
    title: p.title,
    summary: `Target: ${p.target_object_name || p.target_object_id} (${p.target_object_type}) on ${platLabel}. Status: ${p.status.toUpperCase()}${p.decided_by ? ` by ${p.decided_by}` : ''}.`,
    reasoning: p.rationale,
    signals: [
      `Confidence: ${confPct}%`,
      `Expected daily margin impact: ${impact >= 0 ? '+' : ''}${Math.round(impact)} DKK/day`,
      `Priority: ${(p.priority || 'medium').toUpperCase()}`,
    ],
    guardrails: [
      p.is_safe_hygiene ? 'Safe hygiene rule matched (eligible for auto-hygiene)' : 'Requires portfolio solver & cap verification',
      'Checked against +/-15% daily step clamp & monthly 20k DKK max-loss buffer',
    ],
    metrics: {
      impactLabel: impact !== 0 ? `${impact >= 0 ? '+' : ''}${Math.round(impact)} DKK/d` : `${confPct}% conf`,
      confidencePct: confPct,
      platform: p.platform,
    },
    deepLink: '/simplyloan/optimizer',
  };
}

export async function fetchDailyOverview(): Promise<DailyOverviewData> {
  const compass = loadCompassState();
  const baseEntries: AgentActivityEntry[] = [...((shippedAgentLogs as any).entries || [])];

  try {
    const localRaw = localStorage.getItem(LOCAL_AGENT_LOGS_KEY);
    if (localRaw) {
      const parsed = JSON.parse(localRaw);
      if (Array.isArray(parsed)) {
        baseEntries.unshift(...parsed);
      }
    }
  } catch {
    // ignore
  }

  // Parallel live telemetry fetch across all active subsystems
  const [
    adsNoRes,
    adsSeRes,
    seoSeOverview,
    seoNoOverview,
    seoSeContent,
    seoNoContent,
    wctRes,
    unifiedRes,
  ] = await Promise.all([
    fetchGeoOptimizerDashboard('no', 30).catch(() => null),
    fetchGeoOptimizerDashboard('se', 30).catch(() => null),
    seoApi.overview('SE').catch(() => null),
    seoApi.overview('NO').catch(() => null),
    seoApi.content('SE').catch(() => null),
    seoApi.content('NO').catch(() => null),
    dashboardApi.getWctDashboard(undefined, undefined, 7, true).catch(() => null),
    fetch(`${API_BASE_URL}/api/daily-overview`)
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null),
  ]);

  const liveEntries: AgentActivityEntry[] = [];
  const attentionQueue: AttentionItem[] = [];

  if (unifiedRes?.entries && Array.isArray(unifiedRes.entries)) {
    liveEntries.push(...unifiedRes.entries);
  }

  const standingDirectives: AgentStandingDirective[] =
    Array.isArray(unifiedRes?.standingDirectives) && unifiedRes.standingDirectives.length > 0
      ? unifiedRes.standingDirectives
      : [
          {
            id: 'sprint-fox-meta-sole-focus-20261026',
            speakerId: 'fox',
            domain: 'ads',
            geo: 'ALL',
            title: '2-Week Paid Ads Sprint: 100% Sole Focus on Facebook (Meta) Ads',
            directiveText:
              'From Monday Oct 26 for 2 weeks (until Nov 9), Paid Ads sole focus is 100% on Facebook (Meta) Ads. Google & Microsoft RSAs are paused/snoozed. Focus 100% on Meta Creatives & Hooks, Landing Page Testing, and Interactive Funnel Flows.',
            focusChannels: ['meta'],
            suppressedChannels: ['google', 'microsoft'],
            sprintLenses: [
              'Meta Creative Lab: New Ad Angles, Hook Testing & Visual Formats',
              'Landing Page Architecture: /lan-penger/samlelan/ vs. /lanekalkulator/ vs. Social Pre-Lander',
              'Funnel & Flow Mechanics: 2-Step Debt-Consolidation Wizard vs. Static 13-Bank Table',
              'Meta Unit Economics & Partner Clickout Conversion (/slgo/ Offer Rate)',
            ],
            experimentTracks: [
              {
                track: 'Track 1 · Meta Ad Creatives & Hook Angles',
                current_baseline: '3 static Pillow templates (payment_table, comparison_card, loan_slider)',
                testable_ideas: [
                  'Before/After Monthly Cost Breakdown (4 smålån = 6 400 kr/mnd vs. 1 samlelån = 3 290 kr/mnd)',
                  'Problem-aware question hook ("Hvor mye betaler du i doble termingebyrer?")',
                ],
              },
              {
                track: 'Track 2 · Landing Page Variants & Routing',
                current_baseline: 'Cold Meta traffic lands on /lan-penger/samlelan/ with 13 banks',
                testable_ideas: [
                  'Route directly to pre-filled /lanekalkulator/?amount=150000&years=5',
                  'Dedicated 3-lender social landing page (DigiFinans, Sambla, Uno Finans)',
                ],
              },
              {
                track: 'Track 3 · User Flow & Micro-Commitment',
                current_baseline: '1-step static comparison table',
                testable_ideas: [
                  '2-step debt-consolidation micro-wizard before revealing top 3 matching banks',
                ],
              },
            ],
            status: 'active',
            startsAt: '2026-10-26T00:00:00+01:00',
            activeUntil: '2026-11-09T23:59:59+01:00',
          },
        ];

  const suppressedAdsPlatforms = new Set<string>();
  for (const d of standingDirectives) {
    if (d.status === 'active' && (d.speakerId === 'fox' || d.speakerId === 'all' || d.domain === 'ads')) {
      for (const ch of d.suppressedChannels || []) {
        suppressedAdsPlatforms.add(String(ch).toLowerCase());
      }
    }
  }

  // ─── 1. Process Paid Ads Optimizer (NO & SE) ───────────────────────────────
  const processAdsGeo = (resp: GeoOptimizerDashboardResponse | null, geoCode: string) => {
    if (!resp) return;
    const seenRunSummaries = new Set<string>();
    for (const run of (resp.recent_runs || []).slice(0, 8)) {
      if (!run.summary) continue;
      const sumKey = `${run.triggered_by || ''}:${run.summary.slice(0, 80)}`;
      if (seenRunSummaries.has(sumKey)) continue;
      seenRunSummaries.add(sumKey);

      const trig = String(run.triggered_by || '');
      const tierLabel = trig.includes('intraday_guardian')
        ? 'Intraday Guardian (4h)'
        : trig.includes('creative_strategist')
        ? 'Creative Strategist (72h)'
        : 'Daily Portfolio Solver (24h)';

      liveEntries.push({
        id: `ads-run-${geoCode}-${run.id}`,
        timestamp: run.created_at,
        agentName: `Fox · ${tierLabel}`,
        domain: 'ads',
        geo: geoCode,
        actionType: `solver_${run.run_mode}`,
        status: 'applied',
        title: `${geoCode} ${tierLabel} (${run.run_mode}): ${
          run.proposals_count
            ? `${run.proposals_count} proposals (${run.auto_applied_count || 0} auto-applied)`
            : 'Envelope & P&L verified'
        }`,
        summary: `Triggered by ${run.triggered_by || 'EventBridge'} under ${Math.round(
          run.budget_cap_dkk || 2500
        ).toLocaleString()} DKK/day cap.`,
        reasoning: run.summary,
        signals: [
          `Current vs Recommended Daily Spend: ${Math.round(run.current_spend_daily_dkk || 0)} -> ${Math.round(
            run.recommended_spend_daily_dkk || 0
          )} DKK/d`,
          `Mode: ${run.run_mode} · Trigger: ${run.triggered_by || 'EventBridge'}`,
        ],
        guardrails: [
          'Checked against 60,000 DKK monthly budget cap & 20,000 DKK max-loss ceiling',
          'Tracking health & 12h click velocity verified before mutation gate',
        ],
        metrics: {
          impactLabel: run.expected_daily_margin_lift_dkk
            ? `+${Math.round(run.expected_daily_margin_lift_dkk)} DKK/d lift`
            : `${run.run_mode.replace(/_/g, ' ')}`,
        },
        deepLink: '/simplyloan/optimizer',
      });
    }

    // Group bulk applied negative_keyword proposals per platform/day so 277 negatives don't flood the stream
    const negBatches = new Map<string, OptimizerProposal[]>();
    const nonBatchProposals: OptimizerProposal[] = [];

    for (const prop of resp.proposals || []) {
      if (prop.proposal_type === 'negative_keyword' && prop.status === 'applied') {
        const dayKey = `${prop.platform}:${String(prop.decided_at || prop.created_at || '').slice(0, 10)}`;
        negBatches.set(dayKey, [...(negBatches.get(dayKey) || []), prop]);
      } else if (
        (prop.status as string) !== 'superseded' &&
        !(prop.status === 'pending' && suppressedAdsPlatforms.has(String(prop.platform || '').toLowerCase()))
      ) {
        nonBatchProposals.push(prop);
      }
    }

    negBatches.forEach((batch, key) => {
      const first = batch[0];
      const platLabel =
        first.platform === 'google' ? 'Google Ads' : first.platform === 'microsoft' ? 'Microsoft Ads' : 'Meta Ads';
      const sampleTerms = batch
        .slice(0, 5)
        .map((b) => b.title.replace(/^Add Negative.*?:\s*/i, ''))
        .join(', ');
      liveEntries.push({
        id: `ads-neg-batch-${geoCode}-${key}`,
        timestamp: first.decided_at || first.created_at || new Date().toISOString(),
        agentName: `Fox · Search Hygiene (${platLabel})`,
        domain: 'ads',
        geo: geoCode,
        actionType: 'negative_keyword_batch',
        status: 'applied',
        title: `Auto-Applied ${batch.length} Negative Keywords on ${platLabel} (${geoCode})`,
        summary: `Blocked off-intent queries on ${first.target_object_name || platLabel}: ${sampleTerms}${
          batch.length > 5 ? ` + ${batch.length - 5} more` : ''
        }.`,
        reasoning:
          first.rationale ||
          `Excluded off-intent secured mortgage, student loan, and corporate credit search queries so daily budget is strictly reserved for high-RPL unsecured consumer loan & refinancing intent.`,
        signals: [`Total negative keywords applied in batch: ${batch.length}`, `Examples: ${sampleTerms}`],
        guardrails: [
          'Safe hygiene classification (is_safe_hygiene = TRUE)',
          'Core unsecured loan & refinancing clusters protected from negative conflict',
        ],
        metrics: {
          impactLabel: `${batch.length} Negatives`,
          platform: first.platform,
        },
        deepLink: '/simplyloan/optimizer',
      });
    });

    for (const prop of nonBatchProposals.slice(0, 15)) {
      liveEntries.push(mapAdsProposalToEntry(prop));
      if (prop.status === 'pending') {
        const impact = Number(prop.expected_daily_margin_impact_dkk || 0);
        attentionQueue.push({
          id: `attn-ads-${prop.id}`,
          domain: 'ads',
          geo: (prop.geo || geoCode).toUpperCase(),
          priority: prop.priority === 'critical' ? 'critical' : prop.priority === 'high' ? 'high' : 'medium',
          title: prop.title,
          subtitle: `${prop.platform.toUpperCase()} · ${prop.target_object_name || prop.target_object_type}`,
          reasoning: prop.rationale,
          impactLabel: impact !== 0 ? `${impact >= 0 ? '+' : ''}${Math.round(impact)} DKK/d` : undefined,
          deepLink: '/simplyloan/optimizer',
          createdAt: prop.created_at,
        });
      }
    }
  };

  processAdsGeo(adsNoRes, 'NO');
  processAdsGeo(adsSeRes, 'SE');

  // ─── 2. Process SEO Autonomous Engine (SE & NO) ────────────────────────────
  const processSeoMarket = (ov: SeoOverview | null, contentRes: { items: ContentItem[] } | null, market: 'SE' | 'NO') => {
    if (ov) {
      if (ov.lastPublished && ov.lastPublished.publishedAt) {
        liveEntries.push({
          id: `seo-published-${market}-${ov.lastPublished.id}`,
          timestamp: ov.lastPublished.publishedAt,
          agentName: `Eskild · SEO Publisher (${market})`,
          domain: 'seo',
          geo: market,
          actionType: 'seo_publish',
          status: 'applied',
          title: `Published & Verified Live (${market}): «${ov.lastPublished.title}»`,
          summary: `Live URL: ${ov.lastPublished.url || ov.site} · Buffer remaining: ${Math.round(
            ov.buffer?.daysCovered || 0
          )} days (${ov.buffer?.readyCount || 0} ready articles).`,
          reasoning: `Fired during scheduled jittered publishing window after passing Quality Gate, verified HTTP 200 on live URL, and triggered IndexNow / Search Console submission.`,
          signals: [
            `Buffer coverage: ${Math.round(ov.buffer?.daysCovered || 0)} days (${ov.buffer?.readyCount || 0} ready articles)`,
            `Live URL verified: ${ov.lastPublished.url || 'OK'}`,
          ],
          guardrails: [
            'Verified human author byline & statutory credit warning present',
            'Jittered natural publishing window respected',
          ],
          metrics: {
            impactLabel: 'LIVE ON SITE',
          },
          deepLink: '/simplyloan/seo',
        });
      }

      const newsItems = (ov as any).news || [];
      for (const n of newsItems) {
        const score = Number(n.relevance_score || 0);
        const dec = String(n.decision || 'pending');
        liveEntries.push({
          id: `seo-news-${market}-${n.id}`,
          timestamp: n.fetched_at || n.published_at || ov.generatedAt || new Date().toISOString(),
          agentName: `Eskild · SEO News Agent (${market})`,
          domain: 'seo',
          geo: market,
          actionType: `news_${dec}`,
          status: dec === 'breaking' || dec === 'used' ? 'applied' : dec === 'pending' ? 'pending_review' : 'info',
          title: `[${dec.toUpperCase()} · ${score}/100] ${n.title}`,
          summary: `Source: ${n.source_key} · Topic: ${n.topic || 'consumer_credit'} · Decision: ${dec.toUpperCase()}`,
          reasoning:
            n.relevance_reason ||
            (dec === 'ignored'
              ? `Scored ${score}/100 (below the 75/100 borrower-impact threshold), so the agent ignored this headline to save LLM drafting tokens and avoid low-intent content.`
              : `Scored ${score}/100 (>= 75/100 threshold) for direct consumer-loan borrower impact in ${market}; selected for editorial coverage.`),
          signals: [
            `Borrower Relevance Score: ${score}/100 (Threshold: 75)`,
            `Source: ${n.source_key}`,
            `Topic: ${n.topic || 'consumer_credit'}`,
          ],
          guardrails: [
            '5-day topic deduplication window checked',
            'Requires statutory credit warning & verified author before live publish',
          ],
          metrics: {
            impactLabel: `Score ${score}/100`,
            score,
          },
          deepLink: '/simplyloan/seo',
        });
      }

      for (const al of ov.alerts || []) {
        if (al.status === 'open') {
          attentionQueue.push({
            id: `attn-seo-alert-${al.id}`,
            domain: 'seo',
            geo: (al.market || market).toUpperCase(),
            priority: al.severity === 'critical' ? 'critical' : al.severity === 'warning' ? 'high' : 'medium',
            title: al.title,
            subtitle: `SEO Alert (${al.alert_type})`,
            reasoning: JSON.stringify(al.details || {}),
            deepLink: '/simplyloan/seo',
            createdAt: al.last_seen_at,
          });
        }
      }
    }

    if (contentRes?.items) {
      for (const c of contentRes.items.slice(0, 12)) {
        if (c.status === 'review') {
          attentionQueue.push({
            id: `attn-seo-review-${market}-${c.id}`,
            domain: 'seo',
            geo: market,
            priority: 'high',
            title: `Article in Review: «${c.title}»`,
            subtitle: `Keyword: ${c.targetKeyword} · ${c.wordCount} words · Origin: ${c.origin || 'agent'}`,
            reasoning:
              c.blocking && c.blocking.length > 0
                ? `Quality Gate flagged: ${c.blocking.join(', ')}`
                : `Drafted for keyword «${c.targetKeyword}» (${c.searchVolume || 0} monthly searches). Ready for editorial inspection in SEO Hub.`,
            impactLabel: c.qualityScore ? `Gate ${c.qualityScore}/100` : 'In Review',
            deepLink: '/simplyloan/seo',
            createdAt: c.createdAt,
          });
        }
        if (c.origin && c.origin !== 'manual' && c.createdAt) {
          liveEntries.push({
            id: `seo-content-${market}-${c.id}`,
            timestamp: c.createdAt,
            agentName: `Eskild · SEO Content (${c.origin})`,
            domain: 'seo',
            geo: market,
            actionType: `content_${c.status}`,
            status: c.status === 'review' ? 'pending_review' : c.status === 'failed' ? 'blocked' : 'applied',
            title: `Drafted ${c.type.toUpperCase()}: «${c.title}» (${c.status})`,
            summary: `Target keyword: «${c.targetKeyword}» · ${c.wordCount} words · Author: ${c.author || 'Assigned'}`,
            reasoning:
              c.notes ||
              `Generated by ${c.origin} targeting uncovered keyword «${c.targetKeyword}» (search volume: ${
                c.searchVolume || 0
              }). Quality Gate ${c.qualityPassed ? 'PASSED' : 'checked'}.`,
            signals: [
              `Target keyword: ${c.targetKeyword} (vol: ${c.searchVolume || 0})`,
              `Word count: ${c.wordCount} words · Internal links: ${c.internalLinksCount ?? 'verified'}`,
            ],
            guardrails:
              c.blocking && c.blocking.length > 0
                ? c.blocking.map((b) => `Gate blocker: ${b}`)
                : ['Quality Gate checks verified', 'Canonical keyword uniqueness enforced'],
            metrics: {
              impactLabel: c.status.replace(/_/g, ' ').toUpperCase(),
            },
            deepLink: '/simplyloan/seo',
          });
        }
      }
    }
  };

  processSeoMarket(seoSeOverview, seoSeContent, 'SE');
  processSeoMarket(seoNoOverview, seoNoContent, 'NO');

  // ─── 3. Process Email Auto-Scheduler (Live Today Slots from AfimaGlobalCampaigns) ────────────────────
  const emailSentToday = Number(unifiedRes?.emailStats?.sentToday || 0);
  const emailScheduledToday = Number(unifiedRes?.emailStats?.scheduledToday || 0);
  const emailHeldToday = Number(unifiedRes?.emailStats?.heldCount || 0);

  if (Array.isArray(unifiedRes?.heldEmailItems)) {
    for (const held of unifiedRes.heldEmailItems) {
      attentionQueue.push({
        id: `attn-email-${held.id}`,
        domain: 'email',
        geo: held.geo || 'SE',
        priority: 'high',
        title: held.title || 'Held Email Slot',
        subtitle: held.summary || 'Guardrail check requires inspection',
        reasoning: held.reasoning || '',
        impactLabel: held.metrics?.impactLabel,
        deepLink: '/email/auto-schedule',
      });
    }
  }

  // ─── 4. Process Urgent Tasks from Action Center ────────────────────────────
  let urgentTasksCount = 0;
  try {
    const savedTasksRaw = localStorage.getItem(TASKS_STORAGE_KEY);
    const allTasks = savedTasksRaw ? JSON.parse(savedTasksRaw) : (shippedTasks as any).tasks || [];
    const pendingTasks = allTasks.filter((t: any) => t.status === 'pending');
    const urgentTasks = pendingTasks.filter((t: any) => t.priority === 'urgent' || t.priority === 'high');
    urgentTasksCount = urgentTasks.length;

    for (const t of urgentTasks.slice(0, 4)) {
      attentionQueue.push({
        id: `attn-task-${t.id}`,
        domain: 'workspace',
        geo: 'ALL',
        priority: t.priority === 'urgent' ? 'critical' : 'high',
        title: t.title,
        subtitle: `${(t.category || 'general').toUpperCase()} · ${t.provider || t.source || 'Action Center'}${
          t.dueDate ? ` · Due ${t.dueDate}` : ''
        }`,
        reasoning: t.description,
        impactLabel: t.savingsEstimate || undefined,
        deepLink: '/tasks',
      });
    }
  } catch {
    urgentTasksCount = (shippedTasks as any)?.metrics?.urgentCount || 0;
  }

  // ─── 5. Deduplicate & Sort All Agent Activity Entries ──────────────────────
  const seenIds = new Set<string>();
  const mergedEntries: AgentActivityEntry[] = [];
  for (const item of [...liveEntries, ...baseEntries]) {
    if (!item || !item.id || seenIds.has(item.id)) continue;
    seenIds.add(item.id);
    mergedEntries.push(item);
  }
  mergedEntries.sort((a, b) => String(b.timestamp || '').localeCompare(String(a.timestamp || '')));

  // ─── 6. Compute Pulse & Alignment Notes ────────────────────────────────────
  const revToday = wctRes?.today ? Number(wctRes.today.commission || 0) / 100 : 0;
  const revYesterday = wctRes?.yesterday ? Number(wctRes.yesterday.commission || 0) / 100 : 0;
  const rev7d = wctRes?.kpis ? Number(wctRes.kpis.total_revenue || 0) / 100 : 0;
  const convToday = wctRes?.today ? Number(wctRes.today.conversions || 0) : 0;
  const convYesterday = wctRes?.yesterday ? Number(wctRes.yesterday.conversions || 0) : 0;

  const noPnl = adsNoRes?.pnl || adsNoRes?.portfolio?.pnl;
  const noPacingRegime = noPnl?.pacing_regime || 'normal_scale';
  const noMtdSpend = Number(noPnl?.mtd_spend_total_dkk || adsNoRes?.portfolio?.current_spend_daily_dkk || 4537);
  const noMtdRevRiskAdj = Number(noPnl?.mtd_rev_total_risk_adj_dkk || 3913);
  const noRemainingLossBuffer = Number(noPnl?.remaining_loss_buffer_dkk ?? 19376);
  const noTodayCap = Number(noPnl?.today_allowed_daily_budget_dkk || adsNoRes?.settings?.daily_budget_cap_dkk || 2500);
  const noPendingProposals = (adsNoRes?.proposals || []).filter((p) => p.status === 'pending').length;
  const noAppliedProposals = (adsNoRes?.proposals || []).filter((p) => p.status === 'applied').length;

  const seoBufferDaysSe = seoSeOverview?.buffer ? Math.round(seoSeOverview.buffer.daysCovered) : 26;
  const seoBufferDaysNo = seoNoOverview?.buffer ? Math.round(seoNoOverview.buffer.daysCovered) : 30;
  const seoReviewCount =
    Number(seoSeOverview?.pipeline?.review || 0) + Number(seoNoOverview?.pipeline?.review || 0);

  const alignmentNotes: Array<{ tone: 'positive' | 'warning' | 'neutral'; text: string }> = [];

  if (noRemainingLossBuffer >= 14000) {
    alignmentNotes.push({
      tone: 'positive',
      text: `SimplyLoan.no Paid Ads is operating in ${noPacingRegime.replace(/_/g, ' ')} with ${Math.round(
        noRemainingLossBuffer
      ).toLocaleString()} DKK of the 20,000 DKK monthly loss buffer intact (today's cap: ${Math.round(
        noTodayCap
      ).toLocaleString()} DKK).`,
    });
  } else {
    alignmentNotes.push({
      tone: 'warning',
      text: `SimplyLoan.no loss buffer is at ${Math.round(noRemainingLossBuffer).toLocaleString()} DKK (${noPacingRegime.replace(
        /_/g,
        ' '
      )}). Inspect pending negative keyword & bleeder proposals.`,
    });
  }

  if (seoBufferDaysSe < 7 || seoBufferDaysNo < 7) {
    alignmentNotes.push({
      tone: 'warning',
      text: `SEO publishing buffer is below the 7-day target (SE: ${seoBufferDaysSe}d, NO: ${seoBufferDaysNo}d). ${seoReviewCount} article(s) are in review awaiting approval.`,
    });
  } else {
    alignmentNotes.push({
      tone: 'positive',
      text: `SEO publishing buffers are healthy (SE: ${seoBufferDaysSe}d covered, NO: ${seoBufferDaysNo}d covered) with auto-promotion active for drafts passing Quality Gate.`,
    });
  }

  if (emailHeldToday > 0) {
    alignmentNotes.push({
      tone: 'warning',
      text: `Email Auto-Scheduler has ${emailHeldToday} held slot(s) today flagged by pre-flight guardrails — check Auto-Schedule before dispatch window.`,
    });
  } else {
    alignmentNotes.push({
      tone: 'neutral',
      text: `SendEngine v2 is running 80% revenue-weighted scoring across all 8 ESPs with 90/10 Last-Chance probation and 14d/30d auto-pruning active.`,
    });
  }

  // ─── 7. Build the 5 Named Department Managers Summary Strip ───────────────────────
  const pillars: PillarSummary[] = [
    {
      domain: 'ads',
      title: 'Fox — Head of Paid Ads',
      status: noPacingRegime === 'survival_floor' ? 'warning' : noPendingProposals > 0 ? 'attention' : 'healthy',
      headlineMetric: `${Math.round(noTodayCap).toLocaleString()} DKK/d Cap`,
      subMetric: `NO: ${noPacingRegime.replace(/_/g, ' ')} · ${noAppliedProposals} applied / ${noPendingProposals} pending`,
      lastAgentAction: 'Runs 4h search hygiene, 06:20 UTC P&L solver & 72h creative strategist',
      deepLink: '/simplyloan/optimizer',
    },
    {
      domain: 'seo',
      title: 'Eskild — Head of SEO',
      status: seoBufferDaysSe < 7 || seoBufferDaysNo < 7 ? 'warning' : seoReviewCount > 0 ? 'attention' : 'healthy',
      headlineMetric: `SE ${seoBufferDaysSe}d · NO ${seoBufferDaysNo}d`,
      subMetric: `${seoReviewCount} in review · 05:15 UTC Newsjacking + 15m Publisher`,
      lastAgentAction: 'Scans Nordic central banks, grades 100-pt gate & publishes 1.5/d',
      deepLink: '/simplyloan/seo',
    },
    {
      domain: 'email',
      title: 'Bruun — Email Marketing',
      status: emailHeldToday > 0 ? 'warning' : 'healthy',
      headlineMetric:
        emailSentToday + emailScheduledToday > 0
          ? `${emailSentToday} Sent · ${emailScheduledToday} Sched`
          : '10 ESPs Active',
      subMetric: `SendEngine v2 (RPS + UCB) · ${emailHeldToday} held today`,
      lastAgentAction: 'Scores & dispatches daily ESP slots, writes TA1–TA5 copy & triages replies',
      deepLink: '/email/auto-schedule',
    },
    {
      domain: 'deliverability',
      title: 'Frederik Hartmann — Deliverability',
      status: 'healthy',
      headlineMetric: '90/10 Probation',
      subMetric: '14d Suspend · 30d Delete · Campaign-type engagement tests',
      lastAgentAction: 'Monitors open/click engagement by campaign type + 01:30 UTC pruner',
      deepLink: '/email/esp-dashboard',
    },
    {
      domain: 'strategy',
      title: 'Amalie Ryberg — Chief of Staff',
      status: urgentTasksCount > 0 ? 'attention' : 'healthy',
      headlineMetric: `07:05 UTC War Room`,
      subMetric: rev7d > 0 ? `7d Rev: ${Math.round(rev7d).toLocaleString()} DKK · ${urgentTasksCount} tasks` : `${urgentTasksCount} Priority Tasks · Oversight active`,
      lastAgentAction: 'Audits follow-through, challenges cross-channel ideas & checks roundtable ROI',
      deepLink: '/tasks',
    },
  ];

  if (unifiedRes?.compass) {
    if (unifiedRes.compass.northStar) {
      const dbNs = unifiedRes.compass.northStar;
      const dbWeird =
        typeof dbNs.metricLabel === 'string' &&
        !dbNs.metricLabel.includes('Target: >1.50x D30 ROAS') &&
        !dbNs.metricLabel.includes('Microsoft Ads (Bing) in Norway')
          ? dbNs.metricLabel
          : '';
      compass.northStar = {
        ...compass.northStar,
        id: dbNs.id || compass.northStar.id,
        title: dbNs.title || compass.northStar.title,
        pillars: Array.isArray(dbNs.pillars) && dbNs.pillars.length > 0 ? dbNs.pillars : compass.northStar.pillars,
        weirdNote: dbWeird,
      };
    }
    if (Array.isArray(unifiedRes.compass.bets)) {
      compass.bets = unifiedRes.compass.bets;
    }
  }

  const forumThreads: AgentForumThread[] =
    Array.isArray(unifiedRes?.forumThreads) && unifiedRes.forumThreads.length > 0
      ? unifiedRes.forumThreads
      : DEFAULT_FORUM_THREADS;

  // Apply any locally saved decisions if offline
  try {
    const localDecisionsRaw = localStorage.getItem(FORUM_DECISIONS_KEY);
    if (localDecisionsRaw) {
      const localDecs: Record<string, string> = JSON.parse(localDecisionsRaw);
      for (const t of forumThreads) {
        if (localDecs[t.id] && t.status !== 'resolved') {
          t.status = 'resolved';
          t.humanDecision = localDecs[t.id];
          if (!t.messages.some((m) => m.speakerId === 'human')) {
            t.messages.push({
              id: `local-${Date.now()}`,
              speakerId: 'human',
              speakerName: 'Kristoffer (Founder)',
              domain: 'strategy',
              modelUsed: 'human-decision',
              message: localDecs[t.id],
              evidence: ['Founder decision recorded', 'Synced to Business Compass'],
              replyToSpeaker: 'chief_of_staff',
              createdAt: new Date().toISOString(),
            });
          }
        }
      }
    }
  } catch {
    // ignore
  }

  const aiBudget: AiBudgetStatus = unifiedRes?.aiBudget
    ? {
        ...DEFAULT_AI_BUDGET,
        ...unifiedRes.aiBudget,
        agentRoster:
          Array.isArray(unifiedRes.aiBudget.agentRoster) && unifiedRes.aiBudget.agentRoster.length > 0
            ? unifiedRes.aiBudget.agentRoster
            : DEFAULT_AI_BUDGET.agentRoster,
      }
    : DEFAULT_AI_BUDGET;

  const agentReflections: AgentReflectionEntry[] = Array.isArray(unifiedRes?.agentReflections)
    ? unifiedRes.agentReflections
    : [];
  const pendingSteeringNotes: AgentSteeringNote[] = Array.isArray(unifiedRes?.pendingSteeringNotes)
    ? unifiedRes.pendingSteeringNotes
    : [];

  return {
    generatedAt: new Date().toISOString(),
    compass,
    forumThreads,
    agentReflections,
    pendingSteeringNotes,
    standingDirectives,
    soloOperatorView: unifiedRes?.soloOperatorView,
    aiBudget,
    competitorWatch: unifiedRes?.competitorWatch,
    sendoutQaWatch: unifiedRes?.sendoutQaWatch,
    pulse: {
      revenueTodayDkk: revToday,
      revenueYesterdayDkk: revYesterday,
      revenue7dDkk: rev7d,
      conversionsToday: convToday,
      conversionsYesterday: convYesterday,
      noAdsPacingRegime: noPacingRegime,
      noAdsMtdSpendDkk: noMtdSpend,
      noAdsMtdRevRiskAdjDkk: noMtdRevRiskAdj,
      noAdsRemainingLossBufferDkk: noRemainingLossBuffer,
      noAdsTodayCapDkk: noTodayCap,
      noAdsPendingProposals: noPendingProposals,
      noAdsAppliedProposals: noAppliedProposals,
      seoBufferDaysSe,
      seoBufferDaysNo,
      seoReviewCount,
      emailSentToday,
      emailScheduledToday,
      emailHeldToday,
      urgentTasksCount,
      alignmentNotes,
    },
    pillars,
    entries: mergedEntries,
    attentionQueue,
    liveSourcesConnected: {
      ads: Boolean(adsNoRes || adsSeRes),
      seo: Boolean(seoSeOverview || seoNoOverview),
      email: Boolean(unifiedRes?.emailStats),
      revenue: Boolean(wctRes),
    },
  };
}

export async function replyToForumThread(
  threadId: string,
  decision: string,
  addToCompass = true
): Promise<{ ok: boolean; forumThreads?: AgentForumThread[] }> {
  try {
    const raw = localStorage.getItem(FORUM_DECISIONS_KEY);
    const map: Record<string, string> = raw ? JSON.parse(raw) : {};
    map[threadId] = decision;
    localStorage.setItem(FORUM_DECISIONS_KEY, JSON.stringify(map));
  } catch {
    // ignore
  }

  try {
    const resp = await fetch(`${API_BASE_URL}/api/daily-overview/forum/reply`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ threadId, decision, addToCompass }),
    });
    if (resp.ok) {
      return await resp.json();
    }
  } catch {
    // fallback to localStorage
  }
  return { ok: true };
}

export async function triggerForumRoundtable(
  focusTopic?: string,
  sendNotification = true
): Promise<{ ok: boolean; createdThread?: AgentForumThread; forumThreads?: AgentForumThread[]; aiBudget?: AiBudgetStatus }> {
  const resp = await fetch(`${API_BASE_URL}/api/daily-overview/forum/roundtable`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ focusTopic, sendNotification }),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: 'Roundtable request failed' }));
    throw new Error(err.error || 'Roundtable request failed');
  }
  return await resp.json();
}

export async function updateAiBudgetSettings(settings: {
  softCapUsd: number;
  hardCapUsd: number;
  notifyEmail: string;
  notifyOnNeedsHuman: boolean;
  notifyOnDailyDigest: boolean;
  slackWebhookUrl?: string;
  notifySlackOnUrgent?: boolean;
}): Promise<{ ok: boolean; aiBudget?: AiBudgetStatus }> {
  const resp = await fetch(`${API_BASE_URL}/api/daily-overview/budget`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(settings),
  });
  if (!resp.ok) {
    throw new Error('Failed updating AI budget settings');
  }
  return await resp.json();
}

export async function triggerAgentReflection(
  speakerId?: string,
  founderNote?: string
): Promise<{
  ok: boolean;
  createdReflections?: AgentReflectionEntry[];
  agentReflections?: AgentReflectionEntry[];
  pendingSteeringNotes?: AgentSteeringNote[];
  aiBudget?: AiBudgetStatus;
}> {
  const resp = await fetch(`${API_BASE_URL}/api/daily-overview/reflections/run`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ speakerId: speakerId || 'all', founderNote }),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: 'Reflection request failed' }));
    throw new Error(err.error || 'Reflection request failed');
  }
  return await resp.json();
}

export async function submitManagerSteering(
  speakerId: string,
  note: string,
  reflectNow = false
): Promise<{
  ok: boolean;
  createdReflections?: AgentReflectionEntry[];
  agentReflections?: AgentReflectionEntry[];
  pendingSteeringNotes?: AgentSteeringNote[];
  aiBudget?: AiBudgetStatus;
}> {
  const resp = await fetch(`${API_BASE_URL}/api/daily-overview/reflections/steer`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ speakerId, note, reflectNow, pinAsDirective: true }),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: 'Steering request failed' }));
    throw new Error(err.error || 'Steering request failed');
  }
  return await resp.json();
}

export async function executeManagerAction(
  actionType: string,
  targetId: string,
  reflectionId?: number
): Promise<{
  ok: boolean;
  executionResult?: { ok: boolean; message: string };
  soloOperatorView?: SoloOperatorViewData;
  agentReflections?: AgentReflectionEntry[];
}> {
  const resp = await fetch(`${API_BASE_URL}/api/daily-overview/actions/execute`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ actionType, targetId, reflectionId }),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: 'Action execution failed' }));
    throw new Error(err.error || 'Action execution failed');
  }
  return await resp.json();
}


export interface SharedThoughtResult {
  queued?: boolean;
  thought: string;
  assignedTo?: string | null;
  assignedManagerName?: string | null;
  amalieReflection?: AgentReflectionEntry | null;
  assigneeReflection?: AgentReflectionEntry | null;
  compassItemId?: string;
}

export async function shareThoughtWithAmalie(
  thought: string,
  runAssigneeImmediately = false
): Promise<{
  ok: boolean;
  result?: SharedThoughtResult;
  agentReflections?: AgentReflectionEntry[];
  pendingSteeringNotes?: AgentSteeringNote[];
  compass?: BusinessCompassState;
  aiBudget?: AiBudgetStatus;
}> {
  const resp = await fetch(`${API_BASE_URL}/api/daily-overview/thoughts/share`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ thought, runAssigneeImmediately }),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: 'Failed sharing thought with Amalie' }));
    throw new Error(err.error || 'Failed sharing thought with Amalie');
  }
  return await resp.json();
}


export async function sendTestSlackAlert(
  slackWebhookUrl?: string,
  speakerId = 'amalie'
): Promise<{
  ok: boolean;
  slackResult?: {
    ok: boolean;
    sentSlack: boolean;
    slackConfigured: boolean;
    speakerId: string;
    speakerName: string;
    title: string;
    error?: string | null;
  };
  aiBudget?: AiBudgetStatus;
}> {
  const resp = await fetch(`${API_BASE_URL}/api/daily-overview/slack/test`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ slackWebhookUrl, speakerId }),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: 'Failed sending test Slack alert' }));
    throw new Error(err.error || 'Failed sending test Slack alert');
  }
  return await resp.json();
}


export async function runFactCheckAndCleanup(): Promise<{
  ok: boolean;
  factCheckResult?: {
    ok: boolean;
    summary: string;
    changesApplied: string[];
    costUsd?: number;
  };
  compass?: BusinessCompassState;
  agentReflections?: AgentReflectionEntry[];
  forumThreads?: AgentForumThread[];
  aiBudget?: AiBudgetStatus;
}> {
  const resp = await fetch(`${API_BASE_URL}/api/daily-overview/hygiene/clean`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: 'Fact-check cleanup failed' }));
    throw new Error(err.error || 'Fact-check cleanup failed');
  }
  return await resp.json();
}

export async function deleteOverviewItem(
  itemType: 'reflection' | 'compass' | 'thread',
  itemId: string | number
): Promise<{
  ok: boolean;
  compass?: BusinessCompassState;
  agentReflections?: AgentReflectionEntry[];
  forumThreads?: AgentForumThread[];
}> {
  const resp = await fetch(`${API_BASE_URL}/api/daily-overview/hygiene/delete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ itemType, itemId: String(itemId) }),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: 'Failed deleting item' }));
    throw new Error(err.error || 'Failed deleting item');
  }
  return await resp.json();
}


export async function syncCompetitorInboxWatch(
  maxNewResolve = 40
): Promise<{
  ok: boolean;
  syncResult?: {
    ok: boolean;
    emailAccount: string;
    insertedNewEmails: number;
    alreadySyncedCount: number;
    skippedAfimaOwnCount: number;
  };
  competitorWatch?: CompetitorWatchData;
}> {
  const resp = await fetch(`${API_BASE_URL}/api/daily-overview/competitors/sync`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ maxNewResolve }),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: 'Competitor inbox sync failed' }));
    throw new Error(err.error || 'Competitor inbox sync failed');
  }
  return await resp.json();
}


export async function syncSendoutQaWatch(
  maxNewAudit = 12
): Promise<{
  ok: boolean;
  syncResult?: {
    ok: boolean;
    auditedCount: number;
    alreadyAuditedCount: number;
    flaggedCount: number;
    costUsd?: number;
  };
  sendoutQaWatch?: SendoutQaWatchData;
}> {
  const resp = await fetch(`${API_BASE_URL}/api/daily-overview/sendout-qa/sync`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ maxNewAudit }),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: 'Sendout QA sync failed' }));
    throw new Error(err.error || 'Sendout QA sync failed');
  }
  return await resp.json();
}


export async function manageStandingDirective(payload: {
  action?: 'upsert' | 'complete' | 'delete';
  id?: string;
  speakerId?: string;
  directiveText?: string;
  days?: number;
}): Promise<{
  ok: boolean;
  standingDirective?: AgentStandingDirective;
  standingDirectives?: AgentStandingDirective[];
}> {
  const resp = await fetch(`${API_BASE_URL}/api/daily-overview/directives`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: 'Directive update failed' }));
    throw new Error(err.error || 'Directive update failed');
  }
  return await resp.json();
}

export async function updateSoloExperiment(payload: {
  id?: string;
  targetClicks?: number;
  stopLossRule?: string;
  verdictNote?: string;
  status?: string;
  variants?: SoloExperimentVariant[];
}): Promise<{
  ok: boolean;
  soloOperatorView?: SoloOperatorViewData;
}> {
  const resp = await fetch(`${API_BASE_URL}/api/daily-overview/experiment`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: 'Experiment update failed' }));
    throw new Error(err.error || 'Experiment update failed');
  }
  return await resp.json();
}
